import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "../../utils/useScrollAnimation";
import { Category } from "../../../data/resources";

interface ResourcesCategoriesProps {
  categories: Category[];
}

function ResourcesCategories({ categories }: ResourcesCategoriesProps) {
  const { ref, isInView } = useScrollAnimation();
  
  return (
    <section className="py-16 px-4 bg-white" ref={ref}>
      <div className="container mx-auto max-w-6xl">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl font-bold mb-4 text-[#694e42]">Browse Resources by Category</h2>
          <p className="text-lg text-[#8a7266] max-w-2xl mx-auto">
            Explore our comprehensive guides, tutorials, and best practices
          </p>
        </motion.div>
      
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {categories.map((category, index) => (
            <motion.div
              key={category.id}
              className="bg-[#f9f6f4] rounded-xl p-8 border border-[#e2dad3] shadow-sm"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -5, boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.05)" }}
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-[#fff5eb] border border-[#ff8c42]/30 flex items-center justify-center text-[#ff8c42]">
                  {category.icon}
                </div>
                <h3 className="ml-3 text-xl font-bold text-[#694e42]">{category.title}</h3>
              </div>
              <p className="text-[#8a7266] mb-6">{category.description}</p>
              <ul className="space-y-2">
                {category.articles.slice(0, 4).map((article) => (
                  <li key={article.id}>
                    <Link 
                      to={`/resources/${category.id}#${article.id}`} 
                      className="flex items-center text-[#694e42] hover:text-[#ff8c42] transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 mr-2 text-[#ff8c42]">
                        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                      </svg>
                      {article.title}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <Link 
                  to={`/resources/${category.id}`} 
                  className="text-[#ff8c42] font-medium hover:underline flex items-center"
                >
                  View all articles
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 ml-1">
                    <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                  </svg>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default ResourcesCategories;